import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import FilterChipGroup from "@/components/base/FilterChipGroup";
import Pagination from "@/components/base/Pagination";
import NewsCard from "@/components/feature/NewsCard";
import usePageMeta from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";

const REFERENCE_DATE = new Date("2026-10-12");
const PAGE_SIZE = 4;
const DAY = 1000 * 60 * 60 * 24;

const timeOptions = [
  { label: "全部时间", value: "all" },
  { label: "最近一周", value: "week" },
  { label: "最近一个月", value: "month" },
  { label: "最近三个月", value: "quarter" },
];

const typeOptions = [
  { label: "全部类型", value: "all" },
  { label: "新闻", value: "新闻" },
  { label: "活动公告", value: "活动公告" },
];

export default function News() {
  usePageMeta({
    title: "森泽佳奈资讯 - 最新动态与报道",
    description:
      "森泽佳奈最新动态与报道合集，涵盖影视、音乐、活动公告等内容，持续更新她的最新资讯。",
    keywords: "森泽佳奈资讯,森泽佳奈动态,森泽佳奈新闻,森泽佳奈活动",
  });

  const [searchParams] = useSearchParams();
  const queryKeyword = searchParams.get("q") || "";

  const [time, setTime] = useState("all");
  const [type, setType] = useState("all");
  const [keyword, setKeyword] = useState(queryKeyword);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setKeyword(queryKeyword);
  }, [queryKeyword]);

  useEffect(() => {
    setPage(1);
  }, [time, type, keyword]);

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return newsList.filter((item) => {
      if (type !== "all" && item.category !== type) return false;
      if (time !== "all") {
        const diffDays = (REFERENCE_DATE.getTime() - new Date(item.date).getTime()) / DAY;
        if (time === "week" && diffDays > 7) return false;
        if (time === "month" && diffDays > 31) return false;
        if (time === "quarter" && diffDays > 92) return false;
      }
      if (kw && !item.title.includes(kw) && !item.summary.includes(kw) && !item.tags.join("").includes(kw)) {
        return false;
      }
      return true;
    });
  }, [time, type, keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const resetFilters = () => {
    setTime("all");
    setType("all");
    setKeyword("");
    setPage(1);
  };

  return (
    <div className="bg-background-50">
      <section className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb items={[{ label: "资讯" }]} />
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mt-4">
            森泽佳奈最新动态
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mt-3 max-w-2xl">
            汇总森泽佳奈的影视、音乐与活动公告资讯，按时间与类型快速筛选，及时掌握她的最新动态。
          </p>
        </div>
      </section>

      <section className="px-4 md:px-8 py-8 md:py-10">
        <div className="p-4 md:p-5 rounded-lg bg-background-50 border border-background-200 mb-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8 flex-wrap">
              <FilterChipGroup label="时间" options={timeOptions} value={time} onChange={setTime} />
              <FilterChipGroup label="类型" options={typeOptions} value={type} onChange={setType} />
            </div>
            <div className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-md px-3 py-2 md:max-w-sm focus-within:border-primary-300">
              <i className="ri-search-line text-foreground-400 text-sm"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索资讯标题或关键词"
                className="bg-transparent outline-none text-sm text-foreground-800 placeholder:text-foreground-400 w-full"
              />
              {keyword && (
                <button
                  type="button"
                  aria-label="清空搜索"
                  onClick={() => setKeyword("")}
                  className="text-foreground-400 hover:text-primary-600 cursor-pointer"
                >
                  <i className="ri-close-circle-line"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {pageItems.length > 0 ? (
          <>
            <p className="text-sm text-foreground-500 mb-4">共找到 {filtered.length} 条相关资讯</p>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 md:gap-6">
              {pageItems.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
            <div className="mt-8 md:mt-10">
              <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center text-center py-16 md:py-20 border border-dashed border-background-300 rounded-lg">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-inbox-archive-line text-3xl"></i>
            </div>
            <h3 className="font-heading text-lg font-semibold text-foreground-900">
              没有找到符合条件的资讯
            </h3>
            <p className="text-sm text-foreground-500 mt-2">
              试试调整筛选条件或清空关键词后重新查找。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        )}
      </section>
    </div>
  );
}